import React, { useEffect, useMemo } from 'react';
import useProductSliderImpressionTracking from '@hooks/product/use-product-slider-impression-tracking';
import useOnReveal from '@hooks/use-on-reveal';
import { Product } from '@interfaces/models/product';
import ProductBlock, { ProductBlockProps } from '@components/organisms/product-block/product-block';
import useProductQuery from '@hooks/product/use-product-query';

interface PdpProductFeedProps {
  products: Product[];
  productFeedCategory: string;
  title: string;
  productCardProps: ProductBlockProps['productCardProps'];
  swiperProps?: ProductBlockProps['swiperProps'];
  cta?: ProductBlockProps['cta'];
  testId?: string;
  disableOnReveal?: boolean;
}
const PdpProductFeed: React.FC<PdpProductFeedProps> = ({
  products,
  productFeedCategory,
  productCardProps,
  swiperProps,
  cta,
  title,
  testId,
  disableOnReveal = false,
}) => {
  const { product } = useProductQuery();

  const pdpProductData = useMemo(
    () => ({
      // the details of the product on PDP
      product_details: {
        product_id: product.id,
        seller_id: product.seller?.id ?? '',
        product_universe: product.universe?.name ?? '',
        product_category: product.category?.name ?? '',
        product_subcategory: product.subcategory?.name ?? '',
        product_brand: product.brand?.name ?? '',
        product_unit_price: String((product.price?.cents ?? 0) / 100 ?? ''),
        product_country: product?.seller?.countryISO ?? '',
        product_currency: product.price?.currency ?? '',
        product_crossborder_transaction: `${product.dutyAndTax?.cents > 0}`,
        product_ds_eligible: `${product.flags?.includes('direct-shipping')}`,
        product_shipping_method_preselected: '',
        product_shipping_method_selected: '',
      },
    }),
    [product],
  );

  const { onVisibilityChange, triggerInitialRevealTracking, onProductClick } = useProductSliderImpressionTracking({
    productFeedCategory,
    trackingPayload: pdpProductData,
  });

  const { containerRef } = useOnReveal(() => {
    if (!disableOnReveal) {
      triggerInitialRevealTracking();
    }
  }, 0.5);

  useEffect(() => {
    if (disableOnReveal && typeof window !== 'undefined') {
      triggerInitialRevealTracking();
    }
  }, [triggerInitialRevealTracking]);

  return (
    <div
      ref={containerRef}
      data-cy={testId}
    >
      <ProductBlock
        products={products}
        title={title}
        productCardProps={{ ...productCardProps, onClick: onProductClick }}
        swiperProps={swiperProps}
        onVisibilityChange={({ isVisible, index }) => onVisibilityChange({ isVisible, index, products })}
        cta={cta}
        includeProductSchema={false}
        showAuthGuard={true}
      />
    </div>
  );
};

export default PdpProductFeed;
